import { useState, useEffect } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { auth } from '../config/firebase.ts';
import { User } from '../models/User';
import { syncUserData } from '../utils/auth';
import { requestNotificationPermission } from '../utils/messaging';

export const useAuth = () => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fcmRequested, setFcmRequested] = useState(false); // Track if FCM was already requested

  const showAuthError = async (errorMessage: string) => {
    setError(errorMessage);
    const result = await window.confirm(
      `${errorMessage}\n\nIf you are using an ad blocker or privacy extension, please try:\n1. Disabling it for this site\n2. Or using a different browser\n\nWould you like to try logging in again?`
    );
    if (result) {
      await auth.signOut();
      setUserData(null);
      setIsAdmin(false);
      setFirebaseUser(null);
      setError(null);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let authTimeout: NodeJS.Timeout;
    console.log('🔧 useAuth: Setting up auth state listener');

    // Force auth state to resolve after 5 seconds if Firebase doesn't respond
    authTimeout = setTimeout(() => {
      console.log('🔧 useAuth: Auth state timeout - forcing loading to false');
      if (isMounted) {
        setIsLoading(false);
        // Don't reset user state - this breaks Google OAuth redirects
        // setUserData(null);
        // setIsAdmin(false);
        // setFirebaseUser(null);
      }
    }, 5000);

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('🔧 useAuth: Auth state changed -', user ? `User: ${user.email}` : 'No user');
      
      // Clear the timeout since auth state listener fired
      clearTimeout(authTimeout);
      
      if (!isMounted) return;

      // Always update the Firebase user state
      setFirebaseUser(user);
      
      // If no user, reset state and exit early
      if (!user) {
        console.log('🔧 useAuth: No user - setting loading to false');
        setUserData(null);
        setIsAdmin(false);
        setError(null);
        setIsLoading(false);
        return;
      }

      // Only try to sync user data if we have a valid Firebase user
      console.log('🔧 useAuth: User found, syncing data...');
      try {
        const userData = await syncUserData(user);
        console.log('🔧 useAuth: User data synced successfully');
        if (!isMounted) return;
        
        setUserData(userData);
        setIsAdmin(userData.isAdmin || false);
        setError(null);
        
        // TODO: Re-enable FCM once Firebase Messaging API is properly configured
        // FCM currently failing with 401 - needs Google Cloud Console API enablement
        console.log('🔧 useAuth: FCM token registration temporarily disabled');
        // setTimeout(async () => {
        //   try {
        //     console.log('🔧 useAuth: Requesting FCM permission for authenticated user...');
        //     await requestNotificationPermission(userData);
        //   } catch (error) {
        //     console.warn('🔧 useAuth: FCM permission request failed (non-blocking):', error);
        //   }
        // }, 2000);
      } catch (error) {
        console.error('🔧 useAuth: Error fetching user data:', error);
        if (!isMounted) return;

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        if (errorMessage.includes('ERR_BLOCKED_BY_CLIENT')) {
          await showAuthError('Unable to access the database. This might be due to an ad blocker or privacy extension.');
        } else if (errorMessage.includes('Missing or insufficient permissions')) {
          // Handle permission errors gracefully - don't reset user state
          console.warn('Permission error while fetching user data - keeping existing user state');
          // Don't reset userData, isAdmin here - keep existing state
          setError(null); // Don't show error to user for permission issues during logout
        } else if (errorMessage.includes('permission-denied')) {
          // Handle Firestore permission errors - don't reset user state immediately
          console.warn('Firestore permission denied - keeping existing user state');
          // Don't reset user data immediately, just log the error
          setError(null);
        } else {
          // Only reset user state for critical errors
          console.error('Critical auth error:', errorMessage);
          setUserData(null);
          setIsAdmin(false);
          await showAuthError('Error loading user data. Please try again or contact support.');
        }
      } finally {
        console.log('🔧 useAuth: Setting loading to false');
        if (isMounted) {
          setIsLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(authTimeout);
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      await auth.signOut();
      setUserData(null);
      setIsAdmin(false);
      setFirebaseUser(null);
      setError(null);
    } catch (error) {
      console.error('Error during logout:', error);
      setError('Error during logout. Please try again.');
    }
  };

  // Return full user data from Users collection, or null if not loaded
  return { 
    user: userData,
    isLoading, 
    isAdmin, 
    isSuperAdmin: userData?.isSuperAdmin || false,
    logout,
    error
  };
}; 